import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Reads the current vet's GetVideoVet "I am online" state from
 * `vet_availability_now` and keeps it in sync via realtime.
 *
 * Returns null until loaded. Returns false for non-vets / no row.
 */
export const useVetOnlineStatus = (
  userId: string | undefined,
  enabled: boolean,
): { isOnline: boolean; acceptsVideo: boolean; acceptsPhone: boolean } => {
  const [state, setState] = useState({
    isOnline: false,
    acceptsVideo: false,
    acceptsPhone: false,
  });

  useEffect(() => {
    if (!enabled || !userId) {
      setState({ isOnline: false, acceptsVideo: false, acceptsPhone: false });
      return;
    }
    let alive = true;

    const load = async () => {
      const { data } = await supabase
        .from("vet_availability_now")
        .select("is_available, accepts_video, accepts_phone")
        .eq("user_id", userId)
        .maybeSingle();
      if (!alive) return;
      setState({
        isOnline: !!data?.is_available,
        acceptsVideo: !!data?.accepts_video,
        acceptsPhone: !!data?.accepts_phone,
      });
    };

    void load();

    const channel = supabase
      .channel(`vet-availability-self-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "vet_availability_now",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as
            | { is_available?: boolean; accepts_video?: boolean; accepts_phone?: boolean }
            | null;
          if (!row) return;
          setState((prev) => ({
            isOnline:
              typeof row.is_available === "boolean" ? row.is_available : prev.isOnline,
            acceptsVideo:
              typeof row.accepts_video === "boolean" ? row.accepts_video : prev.acceptsVideo,
            acceptsPhone:
              typeof row.accepts_phone === "boolean" ? row.accepts_phone : prev.acceptsPhone,
          }));
        },
      )
      .subscribe();

    return () => {
      alive = false;
      void supabase.removeChannel(channel);
    };
  }, [userId, enabled]);

  return state;
};
